import { useSession } from '@/client/lib/auth-client';

export type SubscriptionTier = 'free' | 'plus' | 'pro';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete' | null;

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  image?: string | null;
  emailVerified: boolean;
  // Subscription fields (optional since they may not be returned by all endpoints)
  subscriptionTier?: SubscriptionTier;
  subscriptionStatus?: SubscriptionStatus;
  currentPeriodEnd?: Date | null;
  cancelAtPeriodEnd?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Tier hierarchy for comparison
 */
const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  free: 0,
  plus: 1,
  pro: 2,
};

/**
 * Check if user has access to a specific tier or higher
 * Handles subscription status and grace period logic
 *
 * @param user - User object (or null if not authenticated)
 * @param requiredTier - Minimum tier required
 * @returns true if user has access
 */
export function hasTier(user: User | null, requiredTier: SubscriptionTier): boolean {
  if (!user) return requiredTier === 'free';

  const tier = user.subscriptionTier ?? 'free';
  const status = user.subscriptionStatus ?? null;

  // Check tier level
  if (TIER_HIERARCHY[tier] < TIER_HIERARCHY[requiredTier]) {
    return false;
  }

  // Free tier is always accessible
  if (requiredTier === 'free') {
    return true;
  }

  // Active or past_due subscriptions have access
  if (status === 'active' || status === 'past_due') {
    return true;
  }

  // Canceled subscriptions still have access until period end
  if (status === 'canceled' && user.currentPeriodEnd) {
    return new Date(user.currentPeriodEnd) > new Date();
  }

  return false;
}

/**
 * Check if user has active Plus subscription (or higher)
 * @deprecated Use hasTier(user, 'plus') for new code
 */
export function isPlusUser(user: User | null): boolean {
  return hasTier(user, 'plus');
}

/**
 * Check if user has active Pro subscription
 */
export function isProUser(user: User | null): boolean {
  return hasTier(user, 'pro');
}

export function useAuth() {
  const { data: session, isPending, error, refetch } = useSession();
  const user = session?.user as User | null;

  // Debug logging in development
  if (typeof window !== 'undefined' && error) {
    console.error('[Auth] Session error:', error);
  }

  return {
    user,
    session: session?.session,
    isLoading: isPending,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isPlusUser: isPlusUser(user),
    isProUser: isProUser(user),
    tier: (user?.subscriptionTier ?? 'free') as SubscriptionTier,
    error,
    refreshSession: refetch,
  };
}
